import React from 'react';
import { DocumentItem, ReconciliationLine, DetailedAnalysis, VerificationSummary } from '../../../services/documentApi';

interface ComparisonTableProps {
    invoice: DocumentItem | null;
    quote: DocumentItem | null;
    onEntityHover?: (entityType: string | null) => void;
    onEntityClick?: (entityType: string | null) => void;
    isEditing?: boolean;
    editedValues?: Record<string, string>;
    onValueChange?: (entityType: string, value: string) => void;
}

// Entity types that can be edited (mapped to PATCH endpoint fields)
const EDITABLE_ENTITIES = new Set([
    'invoice_id', 'invoice_date', 'invoice_type', 'due_date',
    'supplier_name', 'supplier_address', 'supplier_tax_id',
    'supplier_email', 'supplier_phone', 'supplier_website', 'supplier_iban',
    'receiver_name', 'receiver_address',
    'total_amount', 'net_amount', 'total_tax_amount', 'currency',
]);

// Entity type labels in Turkish
const entityLabels: Record<string, string> = {
    'invoice_id': 'Fatura No',
    'invoice_date': 'Fatura Tarihi',
    'invoice_type': 'Fatura Tipi',
    'due_date': 'Vade Tarihi',
    'supplier_name': 'Tedarikçi Adı',
    'supplier_address': 'Tedarikçi Adresi',
    'supplier_email': 'Tedarikçi E-posta',
    'supplier_phone': 'Tedarikçi Telefon',
    'supplier_website': 'Tedarikçi Web',
    'supplier_iban': 'Tedarikçi IBAN',
    'supplier_tax_id': 'Tedarikçi Vergi No',
    'receiver_name': 'Alıcı Adı',
    'receiver_address': 'Alıcı Adresi',
    'total_amount': 'Genel Toplam',
    'net_amount': 'Ara Toplam',
    'total_tax_amount': 'KDV Tutarı',
    'currency': 'Para Birimi',
    'line_item': 'Kalem',
};

// Group entities by category
const entityCategories: Record<string, string[]> = {
    'Fatura Bilgileri': ['invoice_id', 'invoice_date', 'invoice_type', 'due_date'],
    'Tedarikçi': ['supplier_name', 'supplier_address', 'supplier_email', 'supplier_phone', 'supplier_website', 'supplier_iban', 'supplier_tax_id'],
    'Alıcı': ['receiver_name', 'receiver_address'],
    'Tutarlar': ['net_amount', 'total_tax_amount', 'total_amount', 'currency'],
};

const ComparisonTable: React.FC<ComparisonTableProps> = ({ invoice, quote, onEntityHover, onEntityClick, isEditing, editedValues, onValueChange }) => {
    if (!invoice) return <div>Document not found</div>;

    // Get Document AI entities
    const entities = invoice.extracted_data?.entities_with_bounds || [];
    const entityMap = new Map<string, { value: string; confidence: number }>();

    // Build entity map (take first occurrence of each type)
    for (const entity of entities) {
        if (!entityMap.has(entity.type)) {
            entityMap.set(entity.type, {
                value: entity.value,
                confidence: entity.confidence
            });
        }
    }

    // Count line items and extract their prices
    const lineItems = entities.filter(e => e.type === 'line_item');
    const lineItemAmounts = entities.filter(e => e.type === 'line_item/amount');
    const lineItemUnitPrices = entities.filter(e => e.type === 'line_item/unit_price');
    const lineItemQuantities = entities.filter(e => e.type === 'line_item/quantity');

    // Get all IBANs from extracted_data
    const allIbans = invoice.extracted_data?.all_ibans || [];

    // Verification data (if available)
    const lines: ReconciliationLine[] = invoice.comparison?.verification_result?.reconciliation_lines || [];
    const detailedAnalysis: DetailedAnalysis | undefined = invoice.comparison?.verification_result?.detailed_analysis;
    const summary: VerificationSummary | undefined = invoice.comparison?.verification_result?.summary;

    const handleMouseEnter = (entityType: string) => {
        onEntityHover?.(entityType);
    };

    const handleMouseLeave = () => {
        onEntityHover?.(null);
    };

    const getConfidenceBadge = (confidence: number) => {
        const percent = Math.round(confidence * 100);
        const color = percent >= 90 ? '#16a34a' : percent >= 70 ? '#ca8a04' : '#dc2626';
        return (
            <span style={{
                fontSize: '0.7em',
                padding: '1px 6px',
                borderRadius: '8px',
                background: `${color}20`,
                color: color,
                marginLeft: '8px'
            }}>
                {percent}%
            </span>
        );
    };

    return (
        <div className="comparison-page__table-container" style={{ overflow: 'auto', padding: '12px' }}>
            {/* Document AI Extracted Data */}
            <div style={{ marginBottom: '20px' }}>
                <h3 style={{
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    color: '#1e293b',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <span style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#22c55e',
                        display: 'inline-block'
                    }} />
                    Document AI Çıkarımları
                    <span style={{ fontSize: '0.75em', color: '#64748b', fontWeight: 400 }}>
                        ({entities.length} alan tespit edildi)
                    </span>
                </h3>

                {Object.entries(entityCategories).map(([category, types]) => {
                    const categoryEntities = types.filter(t => entityMap.has(t));
                    if (categoryEntities.length === 0) return null;

                    return (
                        <div key={category} style={{ marginBottom: '16px' }}>
                            <div style={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: '#64748b',
                                marginBottom: '6px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                {category}
                            </div>
                            <div style={{
                                background: 'white',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                overflow: 'hidden'
                            }}>
                                {categoryEntities.map((type, idx) => {
                                    const data = entityMap.get(type)!;
                                    const canEdit = isEditing && EDITABLE_ENTITIES.has(type);
                                    const displayValue = editedValues?.[type] ?? data.value;
                                    return (
                                        <div
                                            key={type}
                                            onMouseEnter={() => handleMouseEnter(type)}
                                            onMouseLeave={handleMouseLeave}
                                            onClick={() => !canEdit && onEntityClick?.(type)}
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '10px 14px',
                                                borderBottom: idx < categoryEntities.length - 1 ? '1px solid #f1f5f9' : 'none',
                                                cursor: canEdit ? 'text' : 'pointer',
                                                transition: 'background 0.15s ease',
                                                background: canEdit ? '#eff6ff' : undefined,
                                            }}
                                            onMouseOver={(e) => { if (!canEdit) e.currentTarget.style.background = '#fef9c3'; }}
                                            onMouseOut={(e) => { if (!canEdit) e.currentTarget.style.background = 'white'; }}
                                        >
                                            <span style={{ fontSize: '0.85rem', color: '#475569' }}>
                                                {entityLabels[type] || type}
                                            </span>
                                            {canEdit ? (
                                                <input
                                                    type="text"
                                                    value={displayValue}
                                                    onChange={(e) => onValueChange?.(type, e.target.value)}
                                                    style={{
                                                        fontSize: '0.9rem',
                                                        fontWeight: 500,
                                                        color: '#1e293b',
                                                        border: '1px solid #93c5fd',
                                                        borderRadius: '4px',
                                                        padding: '4px 8px',
                                                        background: 'white',
                                                        textAlign: 'right',
                                                        width: '55%',
                                                        outline: 'none',
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            ) : (
                                                <span style={{
                                                    fontSize: '0.9rem',
                                                    fontWeight: 500,
                                                    color: '#1e293b',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}>
                                                    {data.value}
                                                    {getConfidenceBadge(data.confidence)}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}

                {/* Line Items Summary */}
                {lineItems.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                        <div style={{
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: '#64748b',
                            marginBottom: '6px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Kalemler ({lineItems.length} adet)
                        </div>
                        <div style={{
                            background: 'white',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            overflow: 'hidden'
                        }}>
                            {lineItems.map((item, idx) => {
                                const amount = lineItemAmounts[idx]?.value;
                                const unitPrice = lineItemUnitPrices[idx]?.value;
                                const qty = lineItemQuantities[idx]?.value;
                                const priceDisplay = amount || unitPrice;
                                return (
                                    <div
                                        key={idx}
                                        onMouseEnter={() => handleMouseEnter('line_item')}
                                        onMouseLeave={handleMouseLeave}
                                        onClick={() => onEntityClick?.('line_item')}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '10px 14px',
                                            borderBottom: idx < lineItems.length - 1 ? '1px solid #f1f5f9' : 'none',
                                            cursor: 'pointer',
                                            transition: 'background 0.15s ease',
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.background = '#fef9c3'}
                                        onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                                    >
                                        <span style={{ fontSize: '0.85rem', color: '#475569', flex: '0 0 auto' }}>
                                            Kalem {idx + 1}
                                        </span>
                                        <span style={{
                                            fontSize: '0.85rem',
                                            color: '#1e293b',
                                            flex: 1,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            margin: '0 8px',
                                            textAlign: 'center',
                                        }}>
                                            {item.value}
                                        </span>
                                        {priceDisplay && (
                                            <span style={{
                                                fontSize: '0.85rem',
                                                fontWeight: 600,
                                                color: '#059669',
                                                flex: '0 0 auto',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {qty && `${qty} x `}{priceDisplay}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* All IBANs (if multiple detected) */}
                {allIbans.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                        <div style={{
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: '#64748b',
                            marginBottom: '6px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Banka Hesapları ({allIbans.length} adet)
                        </div>
                        <div style={{
                            background: 'white',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            overflow: 'hidden'
                        }}>
                            {allIbans.map((iban: any, idx: number) => (
                                <div
                                    key={idx}
                                    onMouseEnter={() => handleMouseEnter('supplier_iban')}
                                    onMouseLeave={handleMouseLeave}
                                    onClick={() => onEntityClick?.('supplier_iban')}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '10px 14px',
                                        borderBottom: idx < allIbans.length - 1 ? '1px solid #f1f5f9' : 'none',
                                        fontSize: '0.85rem',
                                        cursor: 'pointer',
                                        transition: 'background 0.15s ease',
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.background = '#fef9c3'}
                                    onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                                >
                                    <span style={{ color: '#475569' }}>IBAN {idx + 1}</span>
                                    <span style={{ color: '#1e293b', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                        {iban.value}
                                        {iban.source === 'ocr_regex' && (
                                            <span style={{
                                                marginLeft: '8px',
                                                fontSize: '0.7em',
                                                padding: '1px 6px',
                                                borderRadius: '8px',
                                                background: '#fef3c7',
                                                color: '#92400e'
                                            }}>
                                                OCR
                                            </span>
                                        )}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Verification Results (if available) */}
            {lines.length > 0 && (
                <>
                    <h3 style={{
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: '#1e293b',
                        marginBottom: '12px',
                        marginTop: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <span style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#3b82f6',
                            display: 'inline-block'
                        }} />
                        Teklif Karşılaştırması
                    </h3>

                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead style={{ background: '#f1f5f9' }}>
                            <tr>
                                <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Kalem</th>
                                <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Fatura</th>
                                <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Teklif</th>
                                <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Durum</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lines.map((row: ReconciliationLine, idx: number) => {
                                const isMatch = row.status === 'match';
                                const statusColor = isMatch ? '#dcfce7' : '#fee2e2';
                                const statusText = isMatch ? '#166534' : '#991b1b';

                                return (
                                    <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: '10px' }}>
                                            <div style={{ fontWeight: 500 }}>{row.item_description || row.description}</div>
                                        </td>
                                        <td style={{ padding: '10px', textAlign: 'right' }}>
                                            {row.invoice_item?.price || row.invoice_value}
                                        </td>
                                        <td style={{ padding: '10px', textAlign: 'right' }}>
                                            {row.quote_item?.price || row.quote_value}
                                        </td>
                                        <td style={{ padding: '10px', textAlign: 'center' }}>
                                            <span style={{
                                                background: statusColor,
                                                color: statusText,
                                                padding: '2px 8px',
                                                borderRadius: '10px',
                                                fontSize: '0.8em',
                                                textTransform: 'capitalize'
                                            }}>
                                                {row.status}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </>
            )}

            {/* AI Analysis */}
            {detailedAnalysis && (
                <div style={{
                    marginTop: '20px',
                    padding: '14px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                }}>
                    <h3 style={{
                        margin: '0 0 12px 0',
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: '#1e293b',
                    }}>
                        AI Analiz Raporu
                    </h3>

                    {detailedAnalysis.overall_assessment && (
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                                Genel Değerlendirme
                            </div>
                            <div style={{
                                fontSize: '0.85rem',
                                color: '#334155',
                                lineHeight: '1.5',
                                padding: '8px 10px',
                                background: 'white',
                                borderRadius: '6px',
                                border: '1px solid #e2e8f0'
                            }}>
                                {detailedAnalysis.overall_assessment}
                            </div>
                        </div>
                    )}

                    {summary && (
                        <div style={{
                            display: 'flex',
                            gap: '10px',
                            flexWrap: 'wrap',
                            marginTop: '12px'
                        }}>
                            <div style={{ padding: '6px 12px', background: '#dcfce7', borderRadius: '6px', fontSize: '0.8rem' }}>
                                <strong>{summary.matches || 0}</strong> Eşleşen
                            </div>
                            <div style={{ padding: '6px 12px', background: '#fee2e2', borderRadius: '6px', fontSize: '0.8rem' }}>
                                <strong>{summary.price_mismatches || 0}</strong> Fiyat Farkı
                            </div>
                            <div style={{ padding: '6px 12px', background: '#e0e7ff', borderRadius: '6px', fontSize: '0.8rem' }}>
                                <strong>{summary.total_items || 0}</strong> Toplam
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ComparisonTable;
